import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Table, Td, Th, TrBody, TrHead } from '../shared/TableElements'
import { FunnelIcon } from '@heroicons/react/20/solid'
import { FunnelIcon as FunnelIconOutline } from '@heroicons/react/24/outline'
import FormatNumericValue from '../shared/FormatNumericValue'
import { ExternalLink } from '@components/shared/ExternalLink'
import { formatCurrencyValue, formatNumericValue } from 'utils/numbers'
import { useViewport } from 'hooks/useViewport'
import { useRecentTrades } from 'hooks/useRecentTrades'
import TradeTime from '../trade/TradeTime'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import {
  getTradeSizeEmoji,
  TRADE_VALUE_FILTER_KEY,
  TRADER_ADDRESS_FILTER_KEY,
  USDC_MINT,
  makeWalletExplorerUrl,
} from 'utils/constants'
import SheenLoader from '@components/loading/SheenLoader'
import ValueFilterFormModal from '@components/modals/ValueFilterFormModal'
import TraderFilterFormModal from '@components/modals/TraderFilterFormModal'
import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import VenueFilterFormModal from '@components/modals/VenueFilterFormModal'
import SideFilterFormModal, {
  SIDE_FILTER_KEY,
} from '@components/modals/SideFilterFormModal'
import { IconButton } from '@components/shared/Button'
import { useVenueFilter } from '../trade/useSettings'
import { usePreferredExplorerUrl } from 'hooks/usePreferredExplorerUrl'
import OpenExplorerLinkIcon from '@components/shared/OpenExplorerLinkIcon'
import store from 'stores/store'
import { useTranslation } from 'next-i18next'

const RecentTradesTable = memo(function RecentTradesTable() {
  const { t } = useTranslation('token-page')
  const { isDesktop } = useViewport()
  const { recentTrades, loading } = useRecentTrades()
  const [showValueFilterForm, setShowValueFilterForm] = useState(false)
  const [showTraderFilterForm, setShowTraderFilterForm] = useState(false)
  const [showVenueFilterForm, setShowVenueFilterForm] = useState(false)
  const [showSideFilterForm, setShowSideFilterForm] = useState(false)
  const quoteToken = store((s) => s.market.quoteToken)
  const { quoteMintPrice } = useMarketTokensWithPrices()
  const [addressToFilter, setAddressToFilter] = useLocalStorage(
    TRADER_ADDRESS_FILTER_KEY,
  )
  const [valueFilter] = useLocalStorage(TRADE_VALUE_FILTER_KEY)
  const [venueFilter] = useVenueFilter()
  const [sideFilter] = useLocalStorage(SIDE_FILTER_KEY)
  const makeExplorerUrlForTransaction = usePreferredExplorerUrl('transaction')

  const handleAddSignerToFilter = useCallback(
    (signer: string) => {
      if (!addressToFilter.includes(signer)) {
        const newTraderFilter = [...addressToFilter, signer]
        setAddressToFilter(newTraderFilter)
      }
    },
    [addressToFilter, setAddressToFilter],
  )

  const prevTradeSignaturesRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    prevTradeSignaturesRef.current = new Set(
      recentTrades.map((trade) => trade.signature),
    )
  }, [recentTrades])

  const renderRows = useMemo(() => {
    return recentTrades.map((trade, i) => {
      const { broker, price, signature, size, signer, taker_side, time } = trade
      const side = taker_side === 'buy' ? 'Buy' : 'Sell'
      const value =
        quoteToken.mint === USDC_MINT
          ? size * price
          : quoteMintPrice
            ? size * price * quoteMintPrice
            : 0

      const isNewTrade = !prevTradeSignaturesRef.current.has(signature)
      const flashClass = isNewTrade
        ? side === 'Buy'
          ? 'green-flash'
          : 'red-flash'
        : ''
      const animationDelay = `${i * 0.05}s`

      return isDesktop ? (
        <TrBody
          className={`${flashClass} ${
            side === 'Buy' ? 'text-th-up' : 'text-th-down'
          }`}
          key={signature}
          style={{ animationDelay }}
        >
          <Td>
            <TradeTime time={time} />
          </Td>
          <Td className="text-left">
            <span>{side}</span>
          </Td>
          <Td className="text-right">
            <span className="text-xl">{getTradeSizeEmoji(value)}</span>
          </Td>
          <Td className="font-mono">
            <div className="flex flex-col items-start">
              {value ? formatCurrencyValue(value) : '–'}
              <span className="text-xs text-th-fgd-4">
                <FormatNumericValue value={size} />
              </span>
            </div>
          </Td>
          <Td className="text-left font-mono">
            <FormatNumericValue value={price} />
          </Td>
          <Td>
            <div className="flex justify-end">
              <ExternalLink
                className={`${side === 'Buy' ? 'text-th-up' : 'text-th-down'}`}
                href={makeWalletExplorerUrl(signer)}
              >
                <div className="flex">
                  <span>{`${signer.slice(0, 5)}...`}</span>
                  <OpenExplorerLinkIcon className="ml-1 size-5" />
                </div>
              </ExternalLink>
              {!addressToFilter.includes(signer) && (
                <IconButton
                  className="ml-2 text-th-fgd-3"
                  onClick={() => handleAddSignerToFilter(signer)}
                  hideBg
                >
                  <FunnelIconOutline className="size-4" />
                </IconButton>
              )}
            </div>
          </Td>
          <Td className="text-right">{broker ? broker.slice(0, -2) : '–'}</Td>
          <Td>
            <div className="flex justify-end">
              <ExternalLink
                className="text-th-fgd-3"
                href={makeExplorerUrlForTransaction(signature)}
              >
                <OpenExplorerLinkIcon className="size-5" />
              </ExternalLink>
            </div>
          </Td>
        </TrBody>
      ) : (
        <div
          className={`flex flex-row items-stretch justify-stretch gap-2 border-b border-th-bkg-3 bg-th-bkg-1 p-4 last:rounded-b-lg last:border-b-0 ${flashClass}`}
          key={signature}
          style={{ animationDelay }}
        >
          <div className="flex flex-col justify-center">
            <p>
              <span className="text-2xl">{getTradeSizeEmoji(value)}</span>
            </p>
          </div>
          <div className="flex flex-1 flex-col">
            <div className="flex flex-row">
              <p className="text-xs text-th-fgd-4">
                <TradeTime time={time} />
              </p>
            </div>
            <div className="flex flex-row justify-between">
              <p
                className={side === 'Buy' ? 'text-th-up' : 'text-th-down'}
              >{`${side} ${formatNumericValue(size)} for ${formatCurrencyValue(
                value,
              )}`}</p>
              <div className="flex items-center">
                <ExternalLink
                  className={`${
                    side === 'Buy' ? 'text-th-up' : 'text-th-down'
                  }`}
                  href={makeWalletExplorerUrl(signer)}
                >
                  <div className="flex">
                    <span className="text-xs">{`${signer.slice(
                      0,
                      5,
                    )}...`}</span>
                    <OpenExplorerLinkIcon className="size-4" />
                  </div>
                </ExternalLink>
                {!addressToFilter.includes(signer) ? (
                  <IconButton
                    className="ml-1 text-th-fgd-3"
                    onClick={() => handleAddSignerToFilter(signer)}
                    hideBg
                  >
                    <FunnelIconOutline className="size-4" />
                  </IconButton>
                ) : null}
              </div>
            </div>
            <div className="flex flex-row justify-between text-xs text-th-fgd-4">
              <p>
                Price: {formatNumericValue(price)} {quoteToken.symbol}
              </p>
              <div className="flex items-center justify-end">
                <p className="text-xs text-th-fgd-4">
                  {broker ? broker.slice(0, -2) : '–'}
                </p>
                <ExternalLink
                  className="ml-0.5 text-th-fgd-3"
                  href={makeExplorerUrlForTransaction(signature)}
                >
                  <OpenExplorerLinkIcon className="size-4" />
                </ExternalLink>
              </div>
            </div>
          </div>
        </div>
      )
    })
  }, [
    addressToFilter,
    handleAddSignerToFilter,
    isDesktop,
    makeExplorerUrlForTransaction,
    quoteMintPrice,
    quoteToken.mint,
    quoteToken.symbol,
    recentTrades,
  ])

  return (
    <>
      {isDesktop ? (
        <Table>
          <thead className="sticky top-0">
            <TrHead>
              <Th className="text-left">{t('recent-trades.time')}</Th>
              <Th className="text-left">
                <div className="flex items-center justify-start">
                  <span className="mr-2">{t('recent-trades.side')}</span>
                  <IconButton
                    className="text-th-fgd-3"
                    onClick={() => setShowSideFilterForm(true)}
                    hideBg
                  >
                    {Object.values(sideFilter).some((value) => !value) &&
                    !showSideFilterForm ? (
                      <FunnelIcon className="size-4 text-th-active" />
                    ) : (
                      <FunnelIconOutline className="size-4" />
                    )}
                  </IconButton>
                </div>
              </Th>
              <Th />
              <Th>
                <div className="flex items-center justify-start">
                  <span className="mr-2">{t('recent-trades.value')}</span>
                  <IconButton
                    className="text-th-fgd-3"
                    onClick={() => setShowValueFilterForm(true)}
                    hideBg
                  >
                    {valueFilter.USDC.min ? (
                      <FunnelIcon className="size-4 text-th-active" />
                    ) : (
                      <FunnelIconOutline className="size-4" />
                    )}
                  </IconButton>
                </div>
              </Th>
              <Th className="text-left">
                {t('recent-trades.price', { symbol: quoteToken.symbol })}
              </Th>
              <Th>
                <div className="flex items-center justify-end">
                  <span className="mr-2">{t('recent-trades.trader')}</span>
                  <IconButton
                    className="text-th-fgd-3"
                    onClick={() => setShowTraderFilterForm(true)}
                    hideBg
                  >
                    {addressToFilter.length && !showTraderFilterForm ? (
                      <FunnelIcon className="size-4 text-th-active" />
                    ) : (
                      <FunnelIconOutline className="size-4" />
                    )}
                  </IconButton>
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex items-center justify-end">
                  <span className="mr-2">{t('recent-trades.venue')}</span>
                  <IconButton
                    className="text-th-fgd-3"
                    onClick={() => setShowVenueFilterForm(true)}
                    hideBg
                  >
                    {Object.values(venueFilter).some((value) => !value) &&
                    !showVenueFilterForm ? (
                      <FunnelIcon className="size-4 text-th-active" />
                    ) : (
                      <FunnelIconOutline className="size-4" />
                    )}
                  </IconButton>
                </div>
              </Th>
              <Th className="text-right">{t('recent-trades.txn')}</Th>
            </TrHead>
          </thead>
          <tbody>{renderRows}</tbody>
        </Table>
      ) : (
        <div className="thin-scroll max-h-[520px] overflow-y-auto rounded-b-lg">
          {renderRows}
        </div>
      )}

      {loading && (
        <div>
          {Array.from({ length: 8 }, (_, i) => (
            <SheenLoader className="flex flex-1 rounded-none" key={i}>
              <div
                className={`h-14 w-full ${
                  i % 2 ? 'bg-th-bkg-2' : 'bg-th-bkg-3'
                }`}
              />
            </SheenLoader>
          ))}
        </div>
      )}

      {!loading && !recentTrades?.length && (
        <div className="m-4 flex items-center justify-center rounded-xl border border-th-bkg-4 p-6">
          <p>{t('recent-trades.no-trades')}</p>
        </div>
      )}

      {showValueFilterForm && (
        <ValueFilterFormModal
          isOpen={showValueFilterForm}
          onClose={() => setShowValueFilterForm(false)}
        />
      )}

      {showTraderFilterForm && (
        <TraderFilterFormModal
          isOpen={showTraderFilterForm}
          onClose={() => setShowTraderFilterForm(false)}
        />
      )}

      {showVenueFilterForm && (
        <VenueFilterFormModal
          isOpen={showVenueFilterForm}
          onClose={() => setShowVenueFilterForm(false)}
        />
      )}

      {showSideFilterForm && (
        <SideFilterFormModal
          isOpen={showSideFilterForm}
          onClose={() => setShowSideFilterForm(false)}
        />
      )}
    </>
  )
})

export default RecentTradesTable
